import { useMemo, useState } from "react";
import { CaretDown, CaretRight, MagnifyingGlass, BookOpen } from "@phosphor-icons/react";

function labelize(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtVal(v) {
  if (Array.isArray(v)) return v.join(" / ");
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(Math.round(v * 10000) / 10000);
  return String(v);
}

export default function TraceView({ trace = {}, docs = {}, order = [] }) {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(() => new Set(order.slice(0, 1)));
  const [showRef, setShowRef] = useState({});

  const modules = useMemo(() => {
    const keys = [...order.filter((k) => trace[k] || docs[k])];
    Object.keys({ ...docs, ...trace }).forEach((k) => { if (!keys.includes(k)) keys.push(k); });
    return keys;
  }, [trace, docs, order]);

  const q = filter.trim().toLowerCase();
  const matchStep = (s) => !q || s.label?.toLowerCase().includes(q) || s.formula?.toLowerCase().includes(q)
    || String(s.substitution ?? "").toLowerCase().includes(q);
  const totalSteps = modules.reduce((n, k) => n + (trace[k]?.length || 0), 0);
  const filteredSteps = q ? modules.reduce((n, k) => n + (trace[k] || []).filter(matchStep).length, 0) : totalSteps;

  const toggle = (k) => setOpen((prev) => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });

  if (!modules.length) {
    return <div className="text-xs text-[var(--c-muted)] p-6" data-testid="trace-empty">No trace recorded — run a calculation first.</div>;
  }

  return (
    <div className="space-y-3" data-testid="trace-view">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--c-muted)]" />
          <input data-testid="trace-filter-input" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter steps — label, formula, value…"
            className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm h-8 pl-7 pr-3 text-xs text-[var(--c-text)] w-72 outline-none focus:border-[var(--c-accent)]" />
        </div>
        <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]" data-testid="trace-step-count">
          {q ? `${filteredSteps} of ${totalSteps}` : totalSteps} steps · {modules.length} modules
        </span>
      </div>

      {modules.map((key) => {
        const steps = (trace[key] || []).filter(matchStep);
        const doc = docs[key];
        const expanded = open.has(key);
        const refOpen = showRef[key];
        return (
          <div key={key} className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm" data-testid={`trace-module-${key}`}>
            <button onClick={() => toggle(key)} data-testid={`trace-module-toggle-${key}`}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[var(--c-border)]/20 transition-colors duration-150">
              {expanded ? <CaretDown size={13} className="text-[var(--c-muted)]" /> : <CaretRight size={13} className="text-[var(--c-muted)]" />}
              <span className="text-xs font-medium text-[var(--c-text)]">{doc?.title || labelize(key)}</span>
              <span className="text-[10px] font-mono text-[var(--c-muted)]">{key}</span>
              <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[var(--c-accent)]/15 text-[var(--c-accent)]">
                {q ? `${steps.length}/${trace[key]?.length || 0}` : trace[key]?.length || 0} steps
              </span>
            </button>

            {expanded && (
              <div className="border-t border-[var(--c-border)] px-4 py-3 space-y-3">
                {doc && (
                  <div>
                    <p className="text-[11px] text-[var(--c-muted)] leading-relaxed">{doc.description}</p>
                    {(doc.references || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {doc.references.map((r) => (
                          <span key={r} className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)]">{r}</span>
                        ))}
                      </div>
                    )}
                    {(doc.formulas || []).length > 0 && (
                      <div className="mt-2">
                        <button onClick={() => setShowRef((s) => ({ ...s, [key]: !s[key] }))} data-testid={`trace-ref-toggle-${key}`}
                          className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] hover:opacity-80">
                          <BookOpen size={12} /> {refOpen ? "Hide" : "Show"} formula reference ({doc.formulas.length})
                        </button>
                        {refOpen && (
                          <div className="mt-2 border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid={`trace-ref-${key}`}>
                            {doc.formulas.map((f, i) => (
                              <div key={i} className="px-3 py-2 border-b border-[var(--c-border)]/50 last:border-0">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                  <span className="text-[11px] text-[var(--c-text)]">{f.label}</span>
                                  <span className="font-mono text-[11px] text-[var(--c-accent)]">{f.formula}</span>
                                </div>
                                {f.notes && <div className="text-[10px] text-[var(--c-muted)] mt-0.5">{f.notes}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {steps.length > 0 ? (
                  <div className="border border-[var(--c-border)] rounded-sm overflow-hidden">
                    <div className="grid grid-cols-[1fr_1.4fr_1.6fr_auto] gap-3 px-3 py-1.5 bg-[var(--c-surface)] border-b border-[var(--c-border)] text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
                      <span>Quantity</span><span>Formula</span><span>Substitution</span><span className="text-right">Value</span>
                    </div>
                    {steps.map((s, i) => (
                      <div key={i} data-testid={`trace-step-${key}-${i}`}
                        className="grid grid-cols-[1fr_1.4fr_1.6fr_auto] gap-3 px-3 py-1.5 border-b border-[var(--c-border)]/40 last:border-0 items-baseline hover:bg-[var(--c-border)]/10">
                        <span className="text-[11px] text-[var(--c-text)]">{s.label}</span>
                        <span className="font-mono text-[10px] text-[var(--c-accent)] break-words">{s.formula}</span>
                        <span className="font-mono text-[10px] text-[var(--c-muted)] break-words">{s.substitution}</span>
                        <span className="font-mono text-[11px] text-[var(--c-green)] text-right whitespace-nowrap">
                          {fmtVal(s.value)}{s.unit && <span className="text-[var(--c-muted)]"> {s.unit}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[11px] text-[var(--c-muted)]">No steps match the filter.</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
